import React, { useState, useEffect, useMemo } from 'react';
import { pagamentiGetCentro, pagamentiSaveCentro, pagamentiFetch } from '../services/pagamentiService';
import '../styles/pagamentiTable.css';

const PagamentiTable = ({ cantiereId }) => {
  const [rows, setRows] = useState([]);
  const [totalePagato, setTotalePagato] = useState(0);
  const [totaleDaPagare, setTotaleDaPagare] = useState(0);
  const [centroCosto, setCentroCosto] = useState('');
  const [message, setMessage] = useState('');
  const [search, setSearch] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: '', direction: 'asc' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const init = async () => {
      try {
        const res = await pagamentiGetCentro(cantiereId);
        setCentroCosto(res.data.centro_costo);
        await refresh(false);
      } catch (err) {
        if (err.response && err.response.status === 404) {
          const centro = window.prompt('Inserisci centro di costo per questo cantiere');
          if (centro) {
            await pagamentiSaveCentro(cantiereId, centro);
            setCentroCosto(centro);
            await refresh(true);
          }
        }
      }
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cantiereId]);

  const refresh = async (force = false) => {
    setLoading(true);
    try {
      const res = await pagamentiFetch(cantiereId, force);
      const data = res.data || [];
      if (data.length === 0) {
        setRows([]);
        setTotalePagato(0);
        setTotaleDaPagare(0);
        setMessage('Nessun pagamento trovato per il centro di costo.');
        return;
      }
      const parsed = data.map((r) => ({
        ...r,
        imponibile: Number(r.imponibile) || 0,
        iva: Number(r.iva) || 0,
        ritenuta: Number(r.ritenuta) || 0,
        totale_con_iva: Number(r.totale_con_iva) || 0,
        totale_pagato: Number(r.totale_pagato) || 0,
        totale_da_pagare: Number(r.totale_da_pagare) || 0,
      }));
      setRows(parsed);
      const pagato = parsed.reduce((s, r) => s + r.totale_pagato, 0);
      const daPagare = parsed.reduce((s, r) => s + r.totale_da_pagare, 0);
      setTotalePagato(pagato);
      setTotaleDaPagare(daPagare);
      setMessage('');
    } catch (err) {
      if (err.response && err.response.status === 404 && err.response.data?.message === 'NO_DOCS_FOR_COST_CENTER') {
        setRows([]);
        setTotalePagato(0);
        setTotaleDaPagare(0);
        setMessage('Nessun pagamento trovato per il centro di costo.');
      } else if (err.response && err.response.status === 401) {
        setMessage('Autorizzazione OAuth richiesta per visualizzare i pagamenti.');
        window.alert('Autorizza l\'app su Fatture in Cloud per procedere.');
      } else {
        console.error('Errore refresh pagamenti', err);
      }
    } finally {
      setLoading(false);
    }
  };

  const cambiaCentro = async () => {
    const nuovo = window.prompt('Modifica centro di costo', centroCosto);
    if (nuovo) {
      await pagamentiSaveCentro(cantiereId, nuovo);
      setCentroCosto(nuovo);
      await refresh(true);
    }
  };

  const filteredRows = useMemo(() => {
    const term = search.toLowerCase();
    if (!term) return rows;
    return rows.filter((r) =>
      (r.fornitore && r.fornitore.toLowerCase().includes(term)) ||
      (r.data_fattura && r.data_fattura.toLowerCase().includes(term)) ||
      (r.categoria && r.categoria.toLowerCase().includes(term)) ||
      ['imponibile', 'iva', 'ritenuta', 'totale_con_iva', 'totale_pagato', 'totale_da_pagare'].some((f) =>
        r[f]?.toString().toLowerCase().includes(term)
      )
    );
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const sortableRows = [...filteredRows];
    if (sortConfig.key) {
      sortableRows.sort((a, b) => {
        const { key, direction } = sortConfig;
        const multiplier = direction === 'asc' ? 1 : -1;
        if (
          ['imponibile', 'iva', 'ritenuta', 'totale_con_iva', 'totale_pagato', 'totale_da_pagare'].includes(
            key
          )
        ) {
          return (a[key] - b[key]) * multiplier;
        }
        if (key === 'data_fattura') {
          return (new Date(a[key]) - new Date(b[key])) * multiplier;
        }
        const valA = (a[key] || '').toString().toLowerCase();
        const valB = (b[key] || '').toString().toLowerCase();
        if (valA < valB) return -1 * multiplier;
        if (valA > valB) return 1 * multiplier;
        return 0;
      });
    }
    return sortableRows;
  }, [filteredRows, sortConfig]);

  useEffect(() => {
    const pagato = filteredRows.reduce((s, r) => s + r.totale_pagato, 0);
    const daPagare = filteredRows.reduce((s, r) => s + r.totale_da_pagare, 0);
    setTotalePagato(pagato);
    setTotaleDaPagare(daPagare);
  }, [filteredRows]);

  const requestSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortArrow = (key) => {
    if (sortConfig.key !== key) return '';
    return sortConfig.direction === 'asc' ? '▲' : '▼';
  };

  return (
    <div>
      <div className="d-flex align-items-center mb-3">
        <strong className="me-2">Centro di costo:</strong>
        <span className="me-3 fw-bold">{centroCosto}</span>
        <button className="btn btn-secondary btn-sm" onClick={cambiaCentro}>Modifica</button>
      </div>
      <div className="d-flex justify-content-end mb-2">
        <div className="me-3"><strong>Totale pagato:</strong> {totalePagato.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
        <div><strong>Totale da pagare:</strong> {totaleDaPagare.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
      </div>
      <div className="mb-3 d-flex">
        <button
          className="btn btn-primary me-3"
          onClick={() => refresh(true)}
          disabled={loading}
        >
          {loading ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Aggiornamento...
            </>
          ) : (
            'Aggiorna'
          )}
        </button>
        <input
          type="text"
          className="form-control"
          placeholder="Cerca..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading && (
        <div className="progress mb-3">
          <div
            className="progress-bar progress-bar-striped progress-bar-animated"
            style={{ width: '100%' }}
          ></div>
        </div>
      )}
      {message && <div className="alert alert-info">{message}</div>}
      <div className="custom-table">
        <div className="custom-table-header">
          <div onClick={() => requestSort('fornitore')} className="sortable">
            Fornitore {getSortArrow('fornitore')}
          </div>
          <div onClick={() => requestSort('fattura_numero')} className="sortable">
            Fattura N. {getSortArrow('fattura_numero')}
          </div>
          <div onClick={() => requestSort('data_fattura')} className="sortable">
            Data Fattura {getSortArrow('data_fattura')}
          </div>
          <div onClick={() => requestSort('categoria')} className="sortable">
            Categoria {getSortArrow('categoria')}
          </div>
          <div onClick={() => requestSort('descrizione')} className="sortable">
            Descrizione {getSortArrow('descrizione')}
          </div>
          <div onClick={() => requestSort('imponibile')} className="sortable">
            Imponibile {getSortArrow('imponibile')}
          </div>
          <div onClick={() => requestSort('iva')} className="sortable">
            IVA {getSortArrow('iva')}
          </div>
          <div onClick={() => requestSort('ritenuta')} className="sortable">
            Rit. Acconto {getSortArrow('ritenuta')}
          </div>
          <div onClick={() => requestSort('totale_con_iva')} className="sortable">
            Totale con IVA {getSortArrow('totale_con_iva')}
          </div>
          <div onClick={() => requestSort('totale_pagato')} className="sortable">
            Totale pagato {getSortArrow('totale_pagato')}
          </div>
          <div onClick={() => requestSort('totale_da_pagare')} className="sortable">
            Totale da pagare {getSortArrow('totale_da_pagare')}
          </div>
        </div>
        {sortedRows.map((r, index) => (
          <div key={`${r.fattura_numero || 'no-num'}-${index}`} className="custom-table-row">
              <abbr className="fornitore-cell" title={r.fornitore}>{r.fornitore}</abbr>
              <div>{r.fattura_numero}</div>
              <div>{r.data_fattura}</div>
              <div>{r.categoria}</div>
              <div className="description-cell" data-full-text={r.descrizione} title={r.descrizione}>{r.descrizione}</div>
              <div>{r.imponibile.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
              <div>{r.iva.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
              <div>{r.ritenuta.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
              <div>{r.totale_con_iva.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
              <div>{r.totale_pagato.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>
              <div>{r.totale_da_pagare.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</div>

          </div>
        ))}
      </div>
    </div>
  );
};


export default PagamentiTable;
